/**
 * Loads a payment processor's browser SDK once per document.
 *
 * One loader for every processor and every surface, for two reasons.
 *
 * **The tag must never be removed on unmount.** Three components used to inject
 * their own `<script>` in a `useEffect` and remove it in the cleanup. The
 * Rainforest bundle is ~1.2MB and served `cache-control: no-cache`, so every
 * injection costs a network round trip at minimum — and removing the tag does
 * not undefine the custom element the script registered, so the removal buys
 * nothing while guaranteeing the next open pays again. Worse, two of those
 * components can be mounted at once (a payment link inside a page that also
 * renders the terminal), and unmounting either one pulled the tag out from
 * under the other.
 *
 * **A second processor needs the same mechanism, not a second one.** The
 * surface contract is one component over two SDKs; if each SDK arrived through
 * its own ad-hoc injection the contract would leak into every call site.
 */

/**
 * Which bundle to load.
 *
 * `RAINFOREST_MERCHANT` is the onboarding SDK rather than a processor - a
 * different bundle defining a different element. It lives in the same map
 * because what is shared is the *mechanism*: load once, never remove, dedupe
 * concurrent callers. Giving it its own injection is what produced the three
 * hand-rolled copies this file replaces.
 */
export type PaymentSdk = "RAINFOREST" | "RAINFOREST_MERCHANT" | "STRIPE";

/** The custom element each SDK defines as it evaluates. */
const ELEMENT_NAME: Record<PaymentSdk, string | null> = {
  RAINFOREST: "rainforest-payment",
  RAINFOREST_MERCHANT: "rainforest-merchant-onboarding",
  // Stripe.js defines no custom element; presence is checked on `window.Stripe`.
  STRIPE: null,
};

const SCRIPT_SRC: Record<PaymentSdk, (sandbox: boolean) => string> = {
  RAINFOREST: (sandbox) =>
    sandbox
      ? "https://static.rainforestpay.com/sandbox.payment.js"
      : "https://static.rainforestpay.com/payment.js",
  RAINFOREST_MERCHANT: (sandbox) =>
    sandbox
      ? "https://static.rainforestpay.com/sandbox.merchant.js"
      : "https://static.rainforestpay.com/merchant.js",
  // Stripe serves one bundle for both modes; the publishable key selects the
  // environment, which is why `sandbox` is unused here.
  STRIPE: () => "https://js.stripe.com/v3/",
};

const pending = new Map<string, Promise<void>>();

const alreadyEvaluated = (sdk: PaymentSdk): boolean => {
  const element = ELEMENT_NAME[sdk];
  if (element) {
    // A defined custom element means a copy of the script has already run —
    // whoever injected it, and whether or not its tag is still in the document.
    return Boolean(window.customElements?.get(element));
  }
  return typeof (window as { Stripe?: unknown }).Stripe !== "undefined";
};

export const paymentSdkSrc = (sdk: PaymentSdk, sandbox: boolean): string =>
  SCRIPT_SRC[sdk](sandbox);

export function loadPaymentSdk(
  sdk: PaymentSdk,
  sandbox: boolean,
): Promise<void> {
  if (typeof window === "undefined") {
    return Promise.resolve();
  }

  if (alreadyEvaluated(sdk)) {
    return Promise.resolve();
  }

  const src = paymentSdkSrc(sdk, sandbox);
  const inFlight = pending.get(src);
  if (inFlight) {
    return inFlight;
  }

  const promise = new Promise<void>((resolve, reject) => {
    const onError = () => {
      // Dropped from the map so a later mount can retry: a failed load is
      // usually a transient network error, and caching the rejection forever
      // would leave the surface permanently broken for that tab.
      pending.delete(src);
      reject(new Error(`Failed to load ${src}`));
    };

    const settle = (script: HTMLScriptElement) => {
      script.addEventListener(
        "load",
        () => {
          script.dataset.paymentSdkState = "loaded";
          resolve();
        },
        { once: true },
      );
      script.addEventListener(
        "error",
        () => {
          script.dataset.paymentSdkState = "failed";
          onError();
        },
        { once: true },
      );
    };

    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${src}"]`,
    );
    if (existing) {
      // A tag that has already settled will never fire again, and attaching a
      // listener to it waits forever - a skeleton on screen until the load
      // watchdog gives up ten seconds later. Several pages still inject these
      // bundles with `next/script`, so a tag someone else owns is a normal
      // thing to find here, not an edge case.
      const state = existing.dataset.paymentSdkState;
      if (state === "loaded" || alreadyEvaluated(sdk)) {
        resolve();
        return;
      }
      if (state === "failed") {
        onError();
        return;
      }
      settle(existing);
      return;
    }

    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    settle(script);
    document.head.appendChild(script);
  });

  pending.set(src, promise);
  return promise;
}

/** Back-compat shim for the Rainforest-only call sites. */
export const loadRainforestScript = (sandbox: boolean): Promise<void> =>
  loadPaymentSdk("RAINFOREST", sandbox);
